// Day-trip data — one dedicated page each at /trips/:id.
// Images reuse the same Unsplash assets as the homepage cards.

import { Link } from "react-router-dom";

const IMG = (id) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400`;

export const TRIPS = [
  {
    id: "bakken",
    name: "Bakken & Dyrehaven",
    tag: "Amusement park & deer forest",
    meta: "25 min by train · Klampenborg",
    mins: 25,
    accent: "#0E7C4A",
    image: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/z5yd1kh3_20261003_172047.webp",
    big: false,
    card:
      "The world's oldest amusement park sits inside a royal deer park — roam ancient oaks and spot wild deer, then ride the rollercoasters.",
    coords: [55.7799, 12.5793],
    blurb:
      "Just north of Copenhagen, you'll find Bakken — the world's oldest amusement park, dating all the way back to 1583.\n\nWhat makes it special is the setting. Bakken sits in Dyrehaven, a former royal hunting ground where ancient oak trees frame paths shared by around 2,000 free-roaming deer. The entire area is a UNESCO World Heritage Site.\n\nUnlike many amusement parks, there's no entrance fee. You simply walk in, soak up the old-school atmosphere and pay for the rides and attractions you choose. Add plenty of green space and a distinctly Danish feel, and Bakken makes for a very different day out from Copenhagen's usual tourist sights.",
    highlights: [
      { name: "Rutschebanen", note: "Ride Bakken's 1932 wooden rollercoaster — one of the oldest still running." },
      { name: "The deer of Dyrehaven", note: "Herds of red and fallow deer roam freely among the oaks — bring a camera." },
      { name: "The Eremitage", note: "A royal hunting lodge on a rise with sweeping views over the park to the sea." },
      { name: "Bellevue Beach", note: "Arne Jacobsen's classic 1930s beach is a short stroll away in summer." },
    ],
    gettingThere: [
      "Take S-train line C to Klampenborg — about 20–25 minutes from København H.",
      "From the station it's a two-minute walk through the park gate into Dyrehaven, and from there it's about a 10-minute walk to Bakken.",
      "Bakken is free to enter; buy ride tickets or an all-day wristband on site.",
    ],
    tip: "Bakken is seasonal — open weekends from April, daily from late May to mid-September, then weekends for Halloween (10–18 & 23–25 Oct) and Christmas (21 Nov–20 Dec).",
    tourId: "689802", // GetYourGuide: city highlights group walk (2h)
  },
  {
    id: "louisiana",
    name: "Louisiana Museum",
    tag: "Modern art on the Øresund",
    meta: "35 min by train · Humlebæk",
    mins: 35,
    accent: "#1E3A8A",
    image: IMG("1720784043091-5d9e73f63609"),
    big: true,
    card:
      "World-class modern art in a sculpture park perched over the Øresund. Arguably the best day trip in Scandinavia.",
    coords: [55.9700, 12.5430],
    blurb:
      "Often called the finest day trip in Scandinavia, the Louisiana Museum of Modern Art is as much about the setting as the art. Glass-walled galleries thread through a coastal park, opening onto a sculpture garden that tumbles down to the Øresund with Sweden shimmering on the horizon. World-class exhibitions, a legendary café terrace and that view make it easy to lose a whole afternoon here.",
    highlights: [
      { name: "The sculpture park", note: "Calder, Miró and Henry Moore works set on lawns above the sea." },
      { name: "Changing exhibitions", note: "Ambitious, world-class shows rotate through the year — check what's on before you go." },
      { name: "The glass corridors", note: "The building itself is a masterclass in Danish modernism, framing the garden at every turn." },
      { name: "Café & terrace", note: "Coffee and cake with one of the best sea views in the country." },
    ],
    gettingThere: [
      "Take the regional train toward Helsingør and get off at Humlebæk — about 35–40 minutes from København H.",
      "From Humlebæk station it's a signposted 10-minute walk to the museum.",
      "Allow 3–4 hours to do it justice.",
    ],
    tip: (
      <>
        Pair it with{" "}
        <Link
          to="/trips/helsingor"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          Helsingør & Kronborg
        </Link>{" "}
        — the castle is 20 minutes further up the same coast line and takes max 2 hours. Do Kronborg
        first, then Louisiana on the way back: both fit one day easily. The Copenhagen Card includes
        entry — worth checking if you have one.
      </>
    ),
    tourId: "470545", // GetYourGuide: Møns Klint & the Forest Tower day tour
  },
  {
    id: "malmo",
    name: "Malmö, Sweden",
    tag: "A second country in 40 minutes",
    meta: "35 min over the bridge",
    mins: 35,
    accent: "#DF3226",
    image: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/77p40ayl_2026-08-18%2023_28_24.374%2B0200.webp",
    big: true,
    card:
      "Tick off a second country. Cross the iconic Øresund Bridge for a Swedish fika, the Turning Torso and a totally different vibe. Bring your passport.",
    coords: [55.6050, 13.0038],
    blurb:
      "Cross the Øresund Bridge and you're in Sweden in well under an hour — a genuinely different country to tick off for the price of a train ticket. Not to talk bad about our neighbours but... it's worth it if you want to tick off Sweden — though Copenhagen has much more to offer that it'd be a shame to spend a whole day here if you've only got three in total.\n\nSweden is lovely, but we'll recommend visiting Stockholm (6h train from Copenhagen, 1 hour flight) or anywhere further north for the nature. If you do cross over, nearby Lund — a charming old university town, makes a lovely alternative or add-on.",
    highlights: [
      { name: "Turning Torso", note: "Calatrava's twisting 190m tower — Scandinavia's tallest building." },
      { name: "Gamla Staden & Lilla Torg", note: "Cobbled squares, half-timbered houses and cosy cafés in the old town." },
      { name: "Ribersborg & the Kallbadhus", note: "A long city beach with an open-air cold-bath house on stilts." },
      { name: "Malmöfestivalen", note: "6–13 August: one of Scandinavia's biggest city festivals — a free week of music, street food and art across the whole city.", url: "https://www.malmofestivalen.se/" },
    ],
    gettingThere: [
      "Take the Öresundståg from København H — about 35–40 minutes across the bridge.",
      "Bring your passport or photo ID: there can be border checks on the Swedish side.",
      "Prices are in Swedish kronor (SEK), though cards are accepted almost everywhere.",
    ],
    tourId: "222942", // GetYourGuide: over the Øresund Bridge to Lund & Malmö
    tip: (
      <>
        A one-way train ticket to Malmö is around 100 DKK. Book it in the DSB app, or use the
        Rejsekort app — it works in Malmö too. See our{" "}
        <Link
          to="/transport"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          transport guide
        </Link>
        .
      </>
    ),
  },
  {
    id: "dragor",
    name: "Dragør",
    tag: "Cobbled fishing village",
    meta: "40 min by bus · Amager",
    mins: 40,
    accent: "#E8590C",
    image: IMG("1704887917222-89176c2a1571"),
    big: false,
    card:
      "A cobbled former fishing village of yellow cottages and hollyhocks. Fresh fish by the harbour, zero crowds.",
    coords: [55.5926, 12.6710],
    blurb:
      "On the southern tip of Amager — a short hop from the city and the airport — Dragør is one of the best-preserved 18th-century villages in Denmark. Its car-free old town is a maze of crooked cobbled lanes, ochre-yellow cottages and hollyhocks, ending at a working harbour where the fish is about as fresh as it gets. It feels a world away from the city, with barely a tour bus in sight.",
    highlights: [
      { name: "The old town lanes", note: "Wander the yellow cottages, picket fences and hollyhocks — beautifully preserved." },
      { name: "The harbour & smokehouse", note: "Fresh and smoked fish right off the boats — lunch with a view." },
    ],
    gettingThere: [
      "Bus 250S runs from the city centre; or take the Metro toward the airport and change to a local bus — about 35–45 minutes in total.",
      "It's very close to the airport, so it's an easy stop on an arrival or departure day.",
    ],
    tip: "Pair the village with a coastal walk — the beach and dyke path just south are lovely in good weather.",
    tourId: "453951", // GetYourGuide: Forest Tower (Skovtårnet) entry ticket
  },
  {
    id: "helsingor",
    name: "Helsingør",
    tag: "Hamlet's castle & the sea",
    meta: "45 min by train · North",
    mins: 45,
    accent: "#0E5C3A",
    image: IMG("1748814670185-ae3f3b8fee7d"),
    big: false,
    card:
      "Hamlet's Kronborg Castle guards the narrowest point to Sweden. Pair it with the brilliant Maritime Museum.",
    coords: [56.0390, 12.6216],
    blurb:
      "At the narrowest point of the Øresund — where Sweden sits just four kilometres across the water — Renaissance Kronborg Castle stands guard. This is Shakespeare's Elsinore, the setting for Hamlet, and a UNESCO World Heritage Site. Next door, the strikingly modern M/S Maritime Museum is built into an old dry dock, and the old town behind is full of atmosphere. It's history, architecture and sea air in one easy trip.",
    highlights: [
      { name: "Kronborg Castle", note: "UNESCO-listed Renaissance fortress — explore the ramparts, ballroom and eerie casemates." },
      { name: "M/S Maritime Museum", note: "An award-winning museum built underground around a former dry dock." },
      { name: "The old town", note: "Cobbled streets, Denmark's oldest pharmacy and cosy harbourside cafés." },
      { name: "Ferry to Sweden", note: "Hop the 20-minute ferry across to Helsingborg for a quick Swedish detour." },
    ],
    gettingThere: [
      "Direct trains run from København H to Helsingør — about 45 minutes.",
      "Kronborg and the Maritime Museum are a 10–15 minute walk from the station, down by the harbour.",
    ],
    tip: (
      <>
        Kronborg itself takes max 2 hours — so the castle and{" "}
        <Link
          to="/trips/louisiana"
          className="text-[#DF3226] font-800 underline underline-offset-2 hover:opacity-80"
        >
          Louisiana
        </Link>{" "}
        fit easily in one day; it just depends how long you give Helsingør town. Do Kronborg first,
        then ride the same coast line back to Humlebæk — the museum stays open late. In summer, look
        out for open-air Hamlet performances staged inside Kronborg's courtyard.
      </>
    ),
    tourId: "38371", // GetYourGuide: grand day trip around Copenhagen
  },
  {
    id: "roskilde",
    name: "Roskilde",
    tag: "Viking ships & the royal cathedral",
    meta: "25 min by train · West",
    mins: 25,
    accent: "#7C2D12",
    image: IMG("1653854309135-c79da0ed007a"),
    big: false,
    card:
      "Denmark's old royal capital: sail-worthy Viking ships you can touch, and the cathedral where 40 kings and queens are buried.",
    coords: [55.6502, 12.0804],
    blurb:
      "Roskilde was Denmark's capital for centuries, and it still carries the weight of it. The twin-spired cathedral — a UNESCO World Heritage Site — is the burial church of some 40 Danish kings and queens, and down at the harbour the Viking Ship Museum displays five 1,000-year-old ships salvaged from the fjord, rebuilt with almost forensic care. It's the easiest 'real Denmark' day trip there is: half an hour by train, everything walkable, and a genuinely lovely old town in between.",
    highlights: [
      { name: "The Viking Ship Museum", note: "Five original Viking ships plus working reconstructions — in summer you can sail one on the fjord." },
      { name: "Roskilde Cathedral", note: "UNESCO-listed brick Gothic masterpiece and the royal burial church — the chapels span 500 years of styles." },
      { name: "The old town", note: "Cobbled streets and cafés around Stændertorvet, the old market square." },
      { name: "The fjord path", note: "A gentle waterside walk from the town down to the museum harbour." },
    ],
    gettingThere: [
      "Regional trains run from København H to Roskilde in about 25 minutes — several per hour.",
      "The cathedral is a 10-minute walk from the station; the Viking Ship Museum another 15 minutes downhill to the fjord.",
      "Allow half a day; a full one if you sail or linger over lunch by the harbour.",
    ],
    tip: "Do the cathedral first, then walk downhill to the ships — the fjord café at the museum is a good lunch stop.",
    tourId: "38371", // GetYourGuide: grand day trip around Copenhagen (incl. Roskilde)
  },
  {
    id: "hillerod",
    name: "Hillerød",
    tag: "Fairy-tale castle & lake walks",
    meta: "40 min by S-train · North",
    mins: 40,
    accent: "#B45309",
    image: "https://thumb.wikimedia.org/wikipedia/commons/thumb/3/32/Frederiksborg_Castle%2C_Hiller%C3%B8d.jpg/1280px-Frederiksborg_Castle%2C_Hiller%C3%B8d.jpg",
    big: false,
    card:
      "Denmark's grandest Renaissance castle on three lake islets, a mirror-calm walk around the castle lake, and a proper little walking street.",
    coords: [55.9349, 12.3006],
    blurb:
      "Hillerød is the easiest 'royal Denmark' day out: one straight S-train ride, and suddenly you're standing in front of Frederiksborg — the grandest Renaissance castle in Scandinavia, floating on three islets in the castle lake. Inside is the Museum of National History, five hundred years of kings, portraits and gilded ceilings. But the town rewards lingering: the path around the lake gives you the postcard view, the walking street is full of cafés and small shops, and there's even a shopping centre by the water for a rainy hour.",
    highlights: [
      { name: "Frederiksborg Castle", url: "https://gyg.me/k6zWqU1Z", note: "The Museum of National History — 500 years of Danish royalty, the gilded chapel and the Baroque garden. Allow 3–4 hours." },
      { name: "The castle lake walk", note: "A gentle loop around Slotsøen with the castle mirrored in the water — the best views are from the far side." },
      { name: "The walking street", note: "Slotsgade and the town centre: cafés, bakeries and small shops — an easy wander between castle and station." },
      { name: "SlotsArkaderne", note: "The town's shopping centre right by the lake — handy for a rainy hour or a Danish high-street browse." },
    ],
    gettingThere: [
      "Take S-train line A to Hillerød — about 40 minutes from København H; it's the end of the line.",
      "From the station it's a 20-minute walk through town on the walking street to the castle.",
      "The castle museum is open daily: 10:00–17:00 Apr–Oct, 11:00–15:00 Nov–Mar.",
    ],
    tip: "Do the castle first, then loop the lake and finish on the walking street — that order walks you gently back toward the station.",
    tourId: "791446", // GetYourGuide: Frederiksborg Slot entrance ticket
  },
];

export const getTrip = (id) => TRIPS.find((t) => t.id === id);
